import { useEffect, useMemo, useState } from "react";
import { Box, SimpleGrid, Stack, Text } from "@mantine/core";
import { capitalizeLeadingCharacter } from "@shared/capitalizeLeadingCharacter";
import {
  activeFillQuestionnaireForm,
  groupEcosystemHeadQuestionsIntoSections,
  localizeEcosystemHeadQuestions,
  localizeEcosystemHeadSections,
  otherTextAnswerKey,
  shouldShowQuestionnaireLanguageToggle,
  type EcosystemHeadQuestion,
  type EcosystemHeadQuestionnaireLanguageId,
  type EcosystemHeadQuestionnaireLanguages,
  type EcosystemHeadQuestionnaireForm,
  type EcosystemHeadSection,
} from "../../../../../shared/ecosystemHeadQuestionnaire";
import EcosystemHeadQuestionField from "./EcosystemHeadQuestionField";
import QuestionnaireLanguageToggle from "./QuestionnaireLanguageToggle";
import { QUESTION_TYPE_META } from "@/pages/ecosystem-head/questionnaire-builder/questionnaireBuilder.const";

type Props = {
  questions: EcosystemHeadQuestion[];
  answers: Record<string, string>;
  onChange: (questionId: string, value: string) => void;
  readOnly?: boolean;
  /** When 2, answerable questions render side-by-side (one row, two cards). */
  columns?: 1 | 2;
  languages?: EcosystemHeadQuestionnaireLanguages | null;
  sections?: EcosystemHeadSection[] | null;
  forms?: Record<string, EcosystemHeadQuestionnaireForm> | null;
  defaultLanguageId?: string | null;
  onFillLanguageChange?: (languageId: string) => void;
};

export default function EcosystemHeadQuestionnaireFillView({
  questions,
  answers,
  onChange,
  readOnly = false,
  columns = 1,
  languages = null,
  sections = null,
  forms = null,
  defaultLanguageId = null,
  onFillLanguageChange,
}: Props) {
  const firstLanguageId = defaultLanguageId ?? languages?.languages[0]?.id ?? "a";
  const [languageSide, setLanguageSide] = useState<EcosystemHeadQuestionnaireLanguageId>(firstLanguageId);
  const showLanguageToggle = shouldShowQuestionnaireLanguageToggle(languages, questions, forms);
  const selectedForm = useMemo(
    () =>
      activeFillQuestionnaireForm(forms, languageSide, {
        questions,
        sections: sections ?? [],
      }),
    [forms, languageSide, questions, sections],
  );
  const visibleQuestions = useMemo(
    () =>
      forms
        ? selectedForm.questions
        : showLanguageToggle
          ? localizeEcosystemHeadQuestions(selectedForm.questions, languageSide, languages)
          : selectedForm.questions,
    [forms, selectedForm.questions, showLanguageToggle, languageSide, languages],
  );
  const visibleSections = useMemo(
    () =>
      forms
        ? selectedForm.sections
        : showLanguageToggle && selectedForm.sections.length > 0
          ? localizeEcosystemHeadSections(selectedForm.sections, languageSide, languages)
          : selectedForm.sections,
    [forms, selectedForm.sections, showLanguageToggle, languageSide, languages],
  );
  const grouped = useMemo(
    () => groupEcosystemHeadQuestionsIntoSections(visibleQuestions, visibleSections),
    [visibleQuestions, visibleSections],
  );
  const showSectionHeadings = visibleSections.length > 0 || grouped.length > 1;

  useEffect(() => {
    if (defaultLanguageId) setLanguageSide(defaultLanguageId);
  }, [defaultLanguageId]);

  const handleLanguageChange = (next: EcosystemHeadQuestionnaireLanguageId) => {
    setLanguageSide(next);
    onFillLanguageChange?.(next);
  };

  const renderQuestion = (question: EcosystemHeadQuestion, displayNumber: number) => {
    if (question.type === "description") {
      return (
        <Box
          key={question.id}
          className="eh-qb-fill-description"
          style={columns === 2 ? { gridColumn: "1 / -1" } : undefined}
        >
          <Text className="eh-qb-fill-description-text">{capitalizeLeadingCharacter(question.label)}</Text>
        </Box>
      );
    }

    const typeMeta = QUESTION_TYPE_META.find((meta) => meta.value === question.type);
    const TypeIcon = typeMeta?.icon;
    const otherKey = otherTextAnswerKey(question.id);

    return (
      <Box key={question.id} className="eh-qb-fill-block" style={columns === 2 ? { height: "100%" } : undefined}>
        <Box className="eh-qb-fill-block-inner">
          <Box className="eh-qb-fill-meta">
            {TypeIcon ? <TypeIcon size={16} stroke={1.6} color="rgba(255,255,255,0.8)" /> : null}
            <Text size="sm" c="rgba(255,255,255,0.8)" fw={600}>
              Question {displayNumber}
              {question.required ? (
                <Text span c="rgba(255,255,255,0.65)" fw={600}>
                  {" "}
                  *
                </Text>
              ) : null}
            </Text>
          </Box>

          <Text className="eh-qb-fill-question-label" c="#ffffff">
            {question.label}
          </Text>

          <Box mt="lg">
            <EcosystemHeadQuestionField
              question={question}
              value={answers[question.id] ?? ""}
              onChange={(value) => onChange(question.id, value)}
              otherValue={answers[otherKey] ?? answers["enq-q1-other"] ?? ""}
              onOtherChange={(value) => onChange(otherKey, value)}
              readOnly={readOnly}
              variant="typeform"
            />
          </Box>
        </Box>
      </Box>
    );
  };

  const questionList = (items: EcosystemHeadQuestion[]) => {
    let displayNumber = 0;
    const rendered = items.map((question) => {
      if (question.type === "description") return renderQuestion(question, 0);
      displayNumber += 1;
      return renderQuestion(question, displayNumber);
    });
    return columns === 2 ? (
      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md" verticalSpacing="md">
        {rendered}
      </SimpleGrid>
    ) : (
      <Stack gap="md">{rendered}</Stack>
    );
  };

  const grid = (
    <Stack gap="xl">
      {grouped.map((section, sectionIndex) => (
        <Stack key={section.id} gap="md">
          {showSectionHeadings ? (
            <Box className="eh-qb-fill-section-heading">
              <Text className="eh-qb-fill-section-kicker">Section {sectionIndex + 1}</Text>
              <Text className="eh-qb-fill-section-title">
                {capitalizeLeadingCharacter(section.title)}
              </Text>
            </Box>
          ) : null}
          {questionList(section.questions)}
        </Stack>
      ))}
    </Stack>
  );

  if (showLanguageToggle && languages) {
    return (
      <Stack gap="md">
        <Stack gap={6} align="flex-end">
          <Text size="lg" c="dimmed">
            Choose your language
          </Text>
          <QuestionnaireLanguageToggle
            languages={languages}
            value={languageSide}
            onChange={handleLanguageChange}
            size="lg"
          />
        </Stack>
        {grid}
      </Stack>
    );
  }

  return grid;
}
